import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { X, TrendingUp, MapPin, Calendar, Building2, ArrowUpRight } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { useLang } from "@/context/LanguageContext";

// 10 flagship handed-over Dubai projects (2020-2025).
// currentPsf = published DLD / DLD Open Data H1 2026 COMMUNITY average
//   (not project-specific quotes). Community averages are also mirrored in the
//   Market Widgets panel and are traceable via Dubai Pulse open data.
// offPlanPsf = developer launch price at project launch year (public press release).
// name_ar / developer_ar follow DLD + RTA official Arabic naming conventions.
// Photos are commercial-free Unsplash + Pexels imagery.
const PROJECTS = [
  { name: "Bluewaters Residences", name_ar: "مساكن بلوواترز", developer: "Meraas", developer_ar: "مراس", area: "Bluewaters Island", launch: "2015", handover: "2019", offPlanPsf: 2200, currentPsf: 3900, communityAvgLabel: "Bluewaters community · DLD H1 2026", communityAvgLabel_ar: "متوسط جزيرة بلوواترز · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.meraas.com/en/destinations/bluewaters-dubai", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/643tbi4u_bluewaters-residence.jpg" },
  { name: "Creek Beach (Address Harbour Point)", name_ar: "شاطئ الخور - العنوان هاربور بوينت", developer: "Emaar", developer_ar: "إعمار", area: "Dubai Creek Harbour", launch: "2017", handover: "2023", offPlanPsf: 1700, currentPsf: 2480, communityAvgLabel: "Dubai Creek Harbour · DLD H1 2026", communityAvgLabel_ar: "متوسط مرسى خور دبي · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.emaar.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/lbs5ecs2_creek%20beach%20%28%20address%20harbour%20point%29.jpg" },
  { name: "City Walk Central Park", name_ar: "سنترال بارك - سيتي ووك", developer: "Meraas", developer_ar: "مراس", area: "City Walk", launch: "2019", handover: "2022", offPlanPsf: 2000, currentPsf: 3100, communityAvgLabel: "City Walk · DLD H1 2026", communityAvgLabel_ar: "متوسط سيتي ووك · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.meraas.com/en/destinations/city-walk", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/jygsqgl5_city%20walk%20central%20park.jpg" },
  { name: "Bulgari Residences", name_ar: "مساكن بولغاري", developer: "Meraas", developer_ar: "مراس", area: "Jumeirah Bay Island", launch: "2014", handover: "2018", offPlanPsf: 4200, currentPsf: 8500, communityAvgLabel: "Jumeirah Bay Island · DLD H1 2026", communityAvgLabel_ar: "متوسط جزيرة جميرا باي · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.meraas.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/v5by25ap_bulgari%20residence.jpg" },
  { name: "The Address Residences Dubai Opera", name_ar: "العنوان ريزيدنسز دبي أوبرا", developer: "Emaar", developer_ar: "إعمار", area: "Downtown Dubai", launch: "2015", handover: "2023", offPlanPsf: 2600, currentPsf: 2950, communityAvgLabel: "Downtown Dubai · DLD H1 2026", communityAvgLabel_ar: "متوسط وسط مدينة دبي · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.emaar.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/k1hubf04_the%20address%20residence%20dubai%20opera.jpg" },
  { name: "Sobha Hartland Waves", name_ar: "شوبا هارتلاند ويفز", developer: "Sobha Realty", developer_ar: "شوبا العقارية", area: "MBR City / Sobha Hartland", launch: "2020", handover: "2023", offPlanPsf: 1300, currentPsf: 1920, communityAvgLabel: "MBR City · DLD H1 2026", communityAvgLabel_ar: "متوسط مدينة محمد بن راشد · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.sobharealty.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/cejf220g_sobha%20hartland%20waves.jpg" },
  { name: "Damac Lagoons, Santorini", name_ar: "بحيرات داماك - سانتوريني", developer: "Damac", developer_ar: "داماك", area: "Damac Lagoons", launch: "2021", handover: "2025", offPlanPsf: 1100, currentPsf: 1800, communityAvgLabel: "Damac Lagoons · DLD H1 2026", communityAvgLabel_ar: "متوسط بحيرات داماك · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.damacproperties.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/aoyyin2s_damac%20lagoons%20santorini.jpg" },
  { name: "Dubai Hills, Golf Grove Villas", name_ar: "فلل غولف غروف - تلال دبي", developer: "Emaar", developer_ar: "إعمار", area: "Dubai Hills Estate", launch: "2019", handover: "2022", offPlanPsf: 1400, currentPsf: 2850, communityAvgLabel: "Dubai Hills villas · DLD H1 2026", communityAvgLabel_ar: "متوسط فلل تلال دبي · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.emaar.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/htb1cb3t_golf%20grove%20villas%20dubai%20hills.jpg" },
  { name: "Palm Tower Residences", name_ar: "مساكن برج النخلة", developer: "Nakheel", developer_ar: "نخيل", area: "Palm Jumeirah", launch: "2015", handover: "2021", offPlanPsf: 2400, currentPsf: 4350, communityAvgLabel: "Palm Jumeirah · DLD H1 2026", communityAvgLabel_ar: "متوسط نخلة جميرا · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.nakheel.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/o9y5l0jq_palm%20tower%20residences.jpg" },
  { name: "Grove (Dubai Creek Harbour)", name_ar: "غروف - مرسى خور دبي", developer: "Emaar", developer_ar: "إعمار", area: "Dubai Creek Harbour", launch: "2019", handover: "2023", offPlanPsf: 1850, currentPsf: 2480, communityAvgLabel: "Dubai Creek Harbour · DLD H1 2026", communityAvgLabel_ar: "متوسط مرسى خور دبي · دائرة الأراضي النصف الأول ٢٠٢٦", projectUrl: "https://www.emaar.com", image: "https://customer-assets-jt897jd0.emergentagent.net/job_dubai-market-intel/artifacts/ue2kkf9k_grove%20dubai%20creek%20harbour.jpg" },
];

// Official RTA/DLD-aligned Arabic community names. Kept flat so cards, chips
// and the project modal all agree on one canonical Arabic label.
const AREA_AR = {
  "Bluewaters Island": "جزيرة بلوواترز",
  "Dubai Creek Harbour": "مرسى خور دبي",
  "City Walk": "سيتي ووك",
  "Jumeirah Bay Island": "جزيرة جميرا باي",
  "Downtown Dubai": "وسط مدينة دبي",
  "MBR City / Sobha Hartland": "مدينة محمد بن راشد",
  "Damac Lagoons": "بحيرات داماك",
  "Dubai Hills Estate": "تلال دبي",
  "Palm Jumeirah": "نخلة جميرا",
};
const AREA_AR_MAP = (name) => AREA_AR[name] || AREA_AR[name.split(" / ")[0]] || name;

const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
const fmtAR = (n) => (n == null ? "" : String(n).replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[+d]));

export default function HandoverCollection() {
  const [active, setActive] = useState(null);
  const { format, currency } = useCurrency();
  const { lang } = useLang();
  const isAR = lang === "ar";
  return (
    <section className="py-12 lg:py-16 relative">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center mb-10 max-w-5xl mx-auto">
          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
            {isAR ? "تسليمات موثّقة" : "Delivered, verified"}
          </p>
          <h2 className="font-editorial text-2xl sm:text-3xl leading-[1.15] text-propertic-ink">
            {isAR ? (
              <>مشاريع تمّ تسليمها مؤخراً، <span className="font-italic-serif text-propertic-gold">ولمحة عمّا تقدّمه دبي.</span></>
            ) : (
              <>Recently delivered, <span className="font-italic-serif text-propertic-gold">a slice of what Dubai offers.</span></>
            )}
          </h2>
          <p className="mt-4 text-propertic-muted text-sm leading-relaxed">
            {isAR
              ? "عشرة مجمّعات عقارية بارزة تمّ تسليمها في دبي خلال السنوات الماضية. اضغطوا على أي بطاقة لعرض المطوّر وتاريخ الإطلاق والتسليم ومؤشّر النموّ."
              : "Ten flagship communities handed over across Dubai in recent years. Tap any card for developer, launch, handover and indicative appreciation."}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {PROJECTS.map((p, i) => {
            const apprPct = Math.round(((p.currentPsf - p.offPlanPsf) / p.offPlanPsf) * 100);
            return (
              <motion.button
                key={p.name}
                type="button"
                data-testid={`handover-card-${i}`}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, y: 24, x: (i % 5 - 2) * 8 }}
                whileInView={{ opacity: 1, y: 0, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i * 0.08), ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[3/4] rounded-2xl overflow-hidden border hairline group text-start bg-propertic-sand hover:border-propertic-gold transition-colors"
              >
                <img
                  src={p.image}
                  alt={`${p.name}, ${p.developer}`}
                  loading="lazy"
                  width="300"
                  height="400"
                  className="ken-burns absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.12]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                {/* Layered gradient for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-propertic-navy via-propertic-navy/60 to-transparent" />
                {/* Developer chip, top start */}
                <span className="absolute top-3 start-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-propertic-navy/70 backdrop-blur-md border border-propertic-gold-2/30">
                  <span className="w-1 h-1 rounded-full bg-propertic-gold-2" />
                  <span className="font-mono-brand text-propertic-gold-2 text-[9px] tracking-[0.16em] uppercase">{isAR ? p.developer_ar : p.developer}</span>
                </span>
                {/* Handover year chip, top end */}
                <span className="absolute top-3 end-3 px-2 py-0.5 rounded-full bg-propertic-paper/12 backdrop-blur-sm border border-propertic-paper/25">
                  <span className="font-mono-brand text-propertic-paper text-[9px] tracking-[0.12em] tabular">{p.handover}</span>
                </span>
                {/* Fancy "tap for details" affordance, pulsing gold ring around
                    an arrow icon. Uses logical `end-3` so it sits bottom-right
                    in LTR and bottom-left in RTL (Arabic). Arrow direction
                    mirrors in RTL so it still points to the outer edge. */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-3 end-3 pointer-events-none"
                >
                  <span className="relative flex items-center justify-center">
                    <span className="absolute w-9 h-9 rounded-full bg-propertic-gold-2/25 opacity-60 animate-ping" />
                    <span className="relative w-8 h-8 rounded-full bg-propertic-gold-2/95 grid place-items-center shadow-[0_6px_16px_rgba(213,171,99,0.55)] transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                      <ArrowUpRight className={`w-3.5 h-3.5 text-propertic-navy ${isAR ? "-scale-x-100" : ""}`} strokeWidth={2.5} />
                    </span>
                  </span>
                </span>
                {/* Bottom editorial block */}
                <div className="relative h-full p-3.5 pt-12 pb-12 sm:p-4 sm:pt-12 flex flex-col justify-end text-propertic-paper">
                  <h3 className="font-editorial text-[14px] sm:text-[15px] leading-[1.15] tracking-[-0.005em] mb-2 line-clamp-3 group-hover:text-propertic-gold-2 transition-colors drop-shadow-sm">
                    {isAR ? (
                      <>
                        <span className="block" dir="rtl">{p.name_ar}</span>
                        <span className="block font-mono-brand font-normal text-[9px] tracking-[0.08em] text-propertic-paper/55 mt-1 truncate" dir="ltr">({p.name})</span>
                      </>
                    ) : p.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px]">
                    <TrendingUp className="w-3 h-3 text-emerald-300" />
                    <span className="tabular font-mono-brand text-emerald-300 tracking-[0.06em]">+{isAR ? fmtAR(apprPct) : apprPct}%</span>
                    <span className="text-propertic-paper/55">·</span>
                    <span className="font-mono-brand text-propertic-paper/70 tracking-[0.12em] uppercase text-[9px]">{isAR ? AREA_AR_MAP(p.area) : p.area.split(" / ")[0]}</span>
                  </div>
                  <p className="mt-1 font-mono-brand text-propertic-paper/45 text-[8.5px] tracking-[0.1em] uppercase leading-tight">
                    {isAR ? "مقارنة بسعر الإطلاق · متوسط المجمّع" : "vs. launch price · community avg"}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <ProjectModal project={active} onClose={() => setActive(null)} />
    </section>
  );
}

function ProjectModal({ project, onClose }) {
  const { format } = useCurrency();
  const { lang, lh } = useLang();
  const isAR = lang === "ar";
  const [apprAnim, setApprAnim] = useState(0);
  if (!project) return null;
  const apprPct = Math.round(((project.currentPsf - project.offPlanPsf) / project.offPlanPsf) * 100);
  return createPortal(
    <AnimatePresence>
      <motion.div
        style={{ zIndex: 90 }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-propertic-navy/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
        onClick={onClose}
        data-testid="handover-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative bg-propertic-paper-2 rounded-[1.5rem] w-full max-w-[440px] max-h-[92dvh] flex flex-col overflow-hidden border hairline shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative w-full aspect-[16/10] shrink-0 overflow-hidden group/hero">
            <img
              src={project.image}
              alt={`${project.name}, ${project.developer}`}
              width="800"
              height="500"
              className="ken-burns w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover/hero:scale-[1.08]"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-propertic-navy/60 via-propertic-navy/10 to-transparent" />
            <button onClick={onClose} data-testid="handover-modal-close" className="absolute top-3 end-3 w-9 h-9 grid place-items-center rounded-full bg-white/90 backdrop-blur hover:bg-propertic-navy hover:text-white transition-colors z-10 shadow-md">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
            <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-1.5">{isAR ? project.developer_ar : project.developer}</p>
            <h3 className="font-editorial text-lg sm:text-xl text-propertic-navy leading-tight">
              {isAR ? (
                <>
                  <span className="block">{project.name_ar}</span>
                  <span className="block font-mono-brand text-[10px] tracking-[0.08em] text-propertic-muted mt-1" dir="ltr">({project.name})</span>
                </>
              ) : project.name}
            </h3>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-propertic-muted">
              <MapPin className="w-3 h-3 text-propertic-gold" />
              <span>{isAR ? AREA_AR_MAP(project.area) : project.area}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Detail icon={<Calendar className="w-3 h-3" />} label={isAR ? "الإطلاق" : "Launched"} value={isAR ? fmtAR(project.launch) : project.launch} />
              <Detail icon={<Calendar className="w-3 h-3" />} label={isAR ? "التسليم" : "Handover"} value={isAR ? fmtAR(project.handover) : project.handover} />
              <Detail label={isAR ? "سعر الإطلاق / قدم²" : "Launch price / sqft"} value={format(project.offPlanPsf, { decimals: 0 })} />
              <Detail label={isAR ? (project.communityAvgLabel_ar || "متوسط سعر القدم² في المجمّع") : (project.communityAvgLabel || "Community avg / sqft")} value={format(project.currentPsf, { decimals: 0 })} accent />
            </div>
            <div className="mt-3 rounded-xl border hairline bg-white p-3 flex items-center justify-between">
              <div>
                <p className="font-mono-brand text-[9px] tracking-[0.2em] uppercase text-propertic-muted mb-0.5">{isAR ? "نموّ المجمّع منذ الإطلاق" : "Community appreciation vs. launch"}</p>
                <p className="font-editorial text-xl text-emerald-600 tabular force-ltr leading-none">
                  <AnimatedCounter target={apprPct} durationMs={900} format={(v) => (isAR ? `+${fmtAR(Math.round(v))}%` : `+${Math.round(v)}%`)} />
                </p>
              </div>
              <TrendingUp className="w-6 h-6 text-emerald-400/60" />
            </div>
            <p className="mt-3 rounded-xl border hairline bg-propertic-sand/40 p-3">
              <span className="block font-mono-brand text-[9px] tracking-[0.2em] uppercase text-propertic-gold mb-1.5">
                {isAR ? "المنهجية" : "Methodology"}
              </span>
              <span className="block text-[10px] text-propertic-muted leading-relaxed">
                {isAR
                  ? <>مؤشّر نموّ المجمّع = ((متوسط سعر القدم² في المجمّع اليوم − سعر الإطلاق للقدم²) ÷ سعر الإطلاق للقدم²) × ١٠٠. <strong>متوسط سعر القدم² الحالي</strong> مأخوذ من التقارير الرسمية لدائرة الأراضي والأملاك (النصف الأول ٢٠٢٦) عبر Dubai Land Department · Open Data، ومتاح للتحقّق على بوابة Dubai Pulse. <strong>سعر الإطلاق</strong> من بيان المطوّر عند طرح المشروع. الأرقام على مستوى المجمّع لا على مستوى الوحدة، مؤشّر توضيحي وليس ضماناً لأداء استثماري. احجزوا استشارة للحصول على تسعير موثّق لوحدة بعينها.</>
                  : <>Community appreciation = ((current community avg PSF − launch PSF) ÷ launch PSF) × 100. <strong>Current PSF</strong> = published community average from Dubai Land Department · Open Data · Dubai Land Department, verifiable on the <a href="https://dubailand.gov.ae/en/open-data/real-estate-data/" target="_blank" rel="noreferrer" className="underline text-propertic-gold hover:text-propertic-gold-2">DLD Open Data · Real-Estate Data</a>. <strong>Launch PSF</strong> = developer's published launch price. Community-level benchmark, not a project-specific quote, indicative reference only, not a guarantee of investment performance. Book a consultation for a verified live quote on a specific unit.</>}
              </span>
            </p>
            <a href={lh("/#consultation")} onClick={onClose} className="mt-4 bg-propertic-navy hover:bg-propertic-navy-2 text-propertic-paper inline-flex items-center gap-2 rounded-full h-10 px-5 text-[11px] font-mono-brand tracking-[0.2em] uppercase w-full justify-center transition-colors">
              {isAR ? (
                <>
                  <span className="hidden sm:inline text-[10px] tracking-[0.06em] whitespace-nowrap">احجز استشارتك المجانية للاستثمار العقاري</span>
                  <span className="sm:hidden">احجز استشارة مجانية</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline text-[9px] tracking-[0.04em] whitespace-nowrap">Book your free real estate investment consultation</span>
                  <span className="sm:hidden">Book Free Consultation</span>
                </>
              )}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function Detail({ icon, label, value, accent }) {
  return (
    <div className={`rounded-lg p-2.5 border hairline ${accent ? "bg-propertic-navy text-propertic-paper border-propertic-navy" : "bg-white"}`}>
      <p className={`font-mono-brand text-[9px] tracking-[0.18em] uppercase mb-1 inline-flex items-center gap-1 ${accent ? "text-propertic-gold-2" : "text-propertic-muted"}`}>
        {icon}{label}
      </p>
      <p className={`font-editorial text-[13px] tabular force-ltr ${accent ? "text-propertic-paper" : "text-propertic-navy"}`}>{value}</p>
    </div>
  );
}

/**
 * Ticks up any number from 0 to `target` over `durationMs` using rAF and
 * ease-out cubic. Re-fires whenever `target` changes (e.g. when a different
 * project modal is opened). Formats via the passed `format` fn.
 */
function AnimatedCounter({ target, durationMs = 900, format = (v) => String(Math.round(v)) }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return <>{format(value)}</>;
}
